import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "src/hooks/useApi";
import { useTranslation } from "react-i18next";
import { Panel, SectionHeader } from "./DashboardPrimitives";

import {
    TbCircleCheck,
    TbEye,
    TbAlertTriangle,
    TbShieldCheck,
    TbMessage2,
    TbRefresh,
    TbQuestionMark,
    TbBuildingBank,
    TbLayoutGrid,
    TbReceipt,
    TbCoins,
} from "react-icons/tb";

// ── Types ──────────────────────────────────────────────────────────────────────

type ActivityType =
    | "invoicePaid"
    | "invoiceViewed"
    | "dunningAdvanced"
    | "portalRegistered"
    | "smsOtpUsed"
    | "questionSubmitted"
    | "questionnaireFilled"
    | "bankTransactionMatched"
    | "billingCharged"
    | "overpaymentDetected";

type ActivityLevel = "info" | "warning" | "urgent";

interface ActivityItem {
    type:               ActivityType;
    level:              ActivityLevel;
    date:               string;
    invoiceId?:         string;
    readableId?:        string;
    patientId?:         string;
    recipientId?:       string;
    recipientName?:     string;
    dunningLevel?:      number;
    daysSinceCreation?: number;
    questionCategory?:  string;
    amount?:            number;
    transactionRef?:    string;
    iban?:              string;
}

// ── Filter chips ───────────────────────────────────────────────────────────────

type FilterKey = "all" | "paid" | "bank" | "dunning" | "portal" | "viewed";

const FILTERS: { key: FilterKey; icon: React.ReactNode; color: string; types: ActivityType[] }[] = [
    { key: "all",     icon: <TbLayoutGrid />,    color: "#6b7280", types: [] },
    { key: "paid",    icon: <TbCircleCheck />,   color: "#22c55e", types: ["invoicePaid"] },
    { key: "bank",    icon: <TbBuildingBank />,  color: "#14b8a6", types: ["bankTransactionMatched", "overpaymentDetected"] },
    { key: "dunning", icon: <TbAlertTriangle />, color: "#ef4444", types: ["dunningAdvanced"] },
    { key: "portal",  icon: <TbShieldCheck />,   color: "#6366f1", types: ["portalRegistered", "questionnaireFilled", "questionSubmitted", "smsOtpUsed", "invoiceViewed"] },
];

// ── Config ─────────────────────────────────────────────────────────────────────

const TYPE_META: Record<ActivityType, { icon: React.ReactNode; color: string }> = {
    invoicePaid:       { icon: <TbCircleCheck />,  color: "#22c55e" },
    invoiceViewed:     { icon: <TbEye />,           color: "#0ea5e9" },
    dunningAdvanced:   { icon: <TbAlertTriangle />, color: "#ef4444" },
    portalRegistered:  { icon: <TbShieldCheck />,   color: "#6366f1" },
    smsOtpUsed:        { icon: <TbMessage2 />,      color: "#14b8a6" },
    questionSubmitted:      { icon: <TbQuestionMark />,   color: "#8b5cf6" },
    questionnaireFilled:    { icon: <TbCircleCheck />,    color: "#6366f1" },
    bankTransactionMatched: { icon: <TbBuildingBank />,   color: "#22c55e" },
    billingCharged:         { icon: <TbReceipt />,        color: "#0ea5e9" },
    overpaymentDetected:    { icon: <TbCoins />,          color: "#1565c0" },
};

function tint(hex: string, alphaHex: string) {
    return `${hex}${alphaHex}`;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function relativeDate(iso: string, t: ReturnType<typeof useTranslation>["t"]): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins  = Math.floor(diff / 60_000);
    const hours = Math.floor(diff / 3_600_000);
    const days  = Math.floor(diff / 86_400_000);
    if (mins  < 2)  return t("briefing.timeJustNow");
    if (mins  < 60) return t("briefing.timeMinAgo",  { n: mins });
    if (hours < 24) return t("briefing.timeHourAgo", { n: hours });
    if (days  < 7)  return t("briefing.timeDayAgo",  { n: days });
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" });
}

// ── Row ────────────────────────────────────────────────────────────────────────

function ActivityRow({ item, onClick }: { item: ActivityItem; onClick?: () => void }) {
    const { t } = useTranslation();
    const meta   = TYPE_META[item.type];

    const urgentColor = item.type === "dunningAdvanced" && (item.dunningLevel ?? 0) >= 3
        ? "#ef4444"
        : meta.color;

    const baseColor = urgentColor;

    return (
        <div
            className={`activity-row${onClick ? " activity-row--clickable" : ""}`}
            style={{
                background:  tint(baseColor, "10"),
                borderColor: tint(baseColor, "28"),
            }}
            onClick={onClick}
        >
            <span className="activity-row__icon" style={{ color: baseColor, background: tint(baseColor, "18"), borderColor: tint(baseColor, "44") }}>
                {meta.icon}
            </span>
            <div className="activity-row__body">
                <span className="activity-row__label">
                    {t(`activity.${item.type}`, {
                        readableId:        item.readableId ?? "",
                        recipientName:     item.recipientName ?? "",
                        dunningLevel:      item.dunningLevel ?? "",
                        daysSinceCreation: item.daysSinceCreation ?? "",
                        questionCategory:  item.questionCategory ? t(`recipientPage.category_${item.questionCategory}`, { defaultValue: item.questionCategory }) : "",
                        amount:            item.amount?.toFixed(2) ?? "",
                        transactionRef:    item.transactionRef ?? "",
                        iban:              item.iban ?? "",
                    })}
                </span>
                {item.recipientName && (
                    <span className="activity-row__sub">{item.recipientName}</span>
                )}
            </div>
            <span className="activity-row__time">{relativeDate(item.date, t)}</span>
        </div>
    );
}

// ── Card ───────────────────────────────────────────────────────────────────────

export function ActivityCard({ refreshTrigger = 0 }: { refreshTrigger?: number }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [refreshing, setRefreshing] = useState(false);
    const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
    const { data, loading, refetch } = useApi<{ items: ActivityItem[]; lookbackDays: number }>(
        "/dashboard/activity",
        refreshTrigger,
    );

    const allItems = (data?.items ?? []).filter((i) => i.type !== "unsentReminder" as string);
    const lookbackDays = data?.lookbackDays ?? 30;

    const activeFilterDef = FILTERS.find(f => f.key === activeFilter)!;
    const items = activeFilter === "all"
        ? allItems
        : allItems.filter(item => activeFilterDef.types.includes(item.type));

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try { await refetch(); } finally { setRefreshing(false); }
    };

    return (
        <Panel>
            <SectionHeader
                title={t("activity.title")}
                meta={!loading ? t("activity.found", { count: allItems.length, n: lookbackDays }) : undefined}
                action={
                    <button className="icon-btn" onClick={handleRefresh} title={t("common.refresh")} disabled={refreshing}>
                        <TbRefresh className={refreshing ? "spin" : ""} />
                    </button>
                }
            />

            <div className="activity-filters">
                {FILTERS.map(f => (
                    <button
                        key={f.key}
                        className={`activity-chip${activeFilter === f.key ? " activity-chip--active" : ""}`}
                        style={{
                            ...(activeFilter === f.key ? { background: f.color + "18", borderColor: f.color + "44" } : undefined),
                            // Always color the icon (even when chip isn't active)
                            ["--chipColor" as any]: f.color,
                        }}
                        onClick={() => setActiveFilter(f.key)}
                        title={t(`activity.filter.${f.key}`)}
                    >
                        <span className="activity-chip__icon" aria-hidden="true">{f.icon}</span>
                        {t(`activity.filter.${f.key}`)}
                    </button>
                ))}
            </div>

            {loading && (
                <div className="list-scroll-region">
                    <div className="activity-list">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="activity-row activity-row--skeleton">
                                <span className="skeleton" style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }} />
                                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                                    <span className="skeleton" style={{ width: "60%", height: 12 }} />
                                    <span className="skeleton" style={{ width: "40%", height: 10 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!loading && items.length === 0 && (
                <div className="activity-empty">
                    <p className="activity-empty__title">{t("activity.empty", { n: lookbackDays })}</p>
                    <p className="activity-empty__hint">{t("activity.emptyHint", { n: lookbackDays })}</p>
                </div>
            )}

            {!loading && items.length > 0 && (
                <div className="list-scroll-region">
                    <div className="activity-list">
                        {items.map((item, i) => (
                            <ActivityRow
                                key={i}
                                item={item}
                                onClick={
                                    item.type === "questionnaireFilled" && (item.patientId ?? item.recipientId)
                                        ? () => navigate(`/patients/${item.patientId ?? item.recipientId}`, { state: { tab: "questionnaire" } })
                                    : item.type === "questionSubmitted" && item.invoiceId
                                        ? () => navigate(`/invoices/${item.invoiceId}`, { state: { tab: "questions" } })
                                    : item.type === "questionSubmitted" && item.recipientId
                                        ? () => navigate(`/patients/${item.recipientId}`, { state: { tab: "questionnaire" } })
                                    : (item.type === "invoiceViewed" || item.type === "portalRegistered" || item.type === "smsOtpUsed") && item.invoiceId
                                        ? () => navigate(`/invoices/${item.invoiceId}`, { state: { tab: "portal" } })
                                    : item.invoiceId
                                        ? () => navigate(`/invoices/${item.invoiceId}`)
                                    : item.recipientId
                                        ? () => navigate(`/patients/${item.recipientId}`)
                                    : undefined
                                }
                            />
                        ))}
                    </div>
                </div>
            )}
        </Panel>
    );
}
